import type { OAuthClientInfo } from "@storyline/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { HttpError } from "@/src/common/services/api-error";
import { completeOAuthAuthorization, fetchOAuthClientInfo } from "@/src/oauth/services/oauth-http.service";

const GENERIC_ERROR = "Une erreur est survenue. Veuillez réessayer.";
const INVALID_REQUEST_ERROR = "Requête d'autorisation invalide ou incomplète.";

const toMessage = (caught: unknown): string => (caught instanceof HttpError ? caught.message : GENERIC_ERROR);

export const useOAuthConsent = () => {
	const location = useLocation();
	const query = useMemo(() => location.search.replace(/^\?/, ""), [location.search]);
	const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const clientId = params.get("client_id");
	const redirectUri = params.get("redirect_uri");
	const isValidRequest = Boolean(clientId && redirectUri && params.get("response_type"));

	const [client, setClient] = useState<OAuthClientInfo | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isApproving, setIsApproving] = useState(false);
	const [error, setError] = useState<string | null>(isValidRequest ? null : INVALID_REQUEST_ERROR);

	useEffect(() => {
		if (!clientId) {
			setIsLoading(false);
			return;
		}
		let cancelled = false;
		fetchOAuthClientInfo(clientId)
			.then((info) => {
				if (!cancelled) {
					setClient(info);
				}
			})
			.catch(() => {})
			.finally(() => {
				if (!cancelled) {
					setIsLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [clientId]);

	const approve = useCallback(async (): Promise<void> => {
		setError(null);
		setIsApproving(true);
		try {
			const { redirectTo } = await completeOAuthAuthorization(query);
			window.location.assign(redirectTo);
		} catch (caught) {
			setError(toMessage(caught));
			setIsApproving(false);
		}
	}, [query]);

	const deny = useCallback((): void => {
		if (!redirectUri) {
			return;
		}
		const target = new URL(redirectUri);
		target.searchParams.set("error", "access_denied");
		const state = params.get("state");
		if (state) {
			target.searchParams.set("state", state);
		}
		window.location.assign(target.toString());
	}, [redirectUri, params]);

	return { client, clientId, redirectUri, isValidRequest, isLoading, isApproving, error, approve, deny };
};
